// ============================================================
//  兑换累加计价纯内核（无框架、无副作用）—— 单测 seam
//  契约：.scratch/prize-redeem-escalation、ADR-014 决策 §2/§3、
//        CONTEXT.md「积分兑换领域」
//
//  (学生 × 奖品) 维度下已兑件数为 c，本次再兑第 r 件（r=c+1…c+qty）单件价：
//    关（escalation null/缺省，恒价/旧数据） → base（基础价 = pointsRequired）
//    step 模式  → min(base + (r-1)·step, cap ?? +∞)
//    tiers 模式 → tiers 中最后一个 from ≤ r 的单价（首行固定 {from:1, price=base}）
//
//  本文件是前端唯一计价缝隙：档位编辑预览（02）、Quote mock 镜像（03）、
//  与服务端 Quote/Redeem 抽样核对（06）都消费这里，避免各组件散落一套口径。
//  金额以服务端实扣为准——本模块只算"规则产生的价"，不参与扣款。
//  将来补自动化测试的唯一目标即本文件。
// ============================================================

/** 累加计价模式：step=简单加价 / tiers=档位表 */
export type EscalationMode = 'step' | 'tiers';

/** 档位表的一行：从第 `from` 件起单价改为 `price` */
export interface EscalationTier {
  /** 起始件序号（1 起），表内升序；首行固定 = 1 */
  from: number;
  /** 该段单件价 */
  price: number;
}

/**
 * 奖品「兑换累加」配置（随 Prize 存取，null/缺省 = 恒价/旧数据）
 *
 * base 不落在此对象内：基础价 = 奖品现有 `pointsRequired`（首件价），
 * 由调用方作为参数传入，避免同一个价格出现两个来源（见 spec 档位首行联动）。
 */
export interface PrizeEscalation {
  /** 计价模式：step 简单加价 / tiers 档位表 */
  mode: EscalationMode;
  /** step 模式：每多 1 件的加价，≥1 整数 */
  step?: number;
  /** step 模式：封顶单价；null / 缺省 = 不封顶 */
  cap?: number | null;
  /** tiers 模式：升序分段；首行固定 {from:1, price=base}，末行开放即封顶 */
  tiers?: EscalationTier[];
}

/**
 * 单件价：对 (学生×奖品) 的件序号 `r`（从 1 起）出该件单价。
 *
 * - `escalation` 为空 → 恒价 `base`（关/旧数据行为完全不变）。
 * - step 模式下缺 step / 未知 mode → 按恒价 `base` 兜底（非法数据不炸、不越界低于 base）。
 */
export function escalationUnitPrice(
  base: number,
  escalation: PrizeEscalation | null | undefined,
  r: number,
): number {
  if (!escalation || r < 1) return base;

  if (escalation.mode === 'step') {
    const step =
      typeof escalation.step === 'number' &&
      Number.isInteger(escalation.step) &&
      escalation.step >= 1
        ? escalation.step
        : 0; // 缺/坏 step → 恒价 base
    const raw = base + (r - 1) * step;
    const cap = escalation.cap;
    if (cap != null && typeof cap === 'number' && Number.isFinite(cap)) {
      // 封顶下限夹到 base：非法 cap（<base，校验本应拦住）也不会把价压到基础价以下
      return Math.max(base, Math.min(raw, cap));
    }
    return raw;
  }

  if (escalation.mode === 'tiers') {
    let price = base; // 首行恒为基础价；表空/未命中按基础价兜底
    const tiers = escalation.tiers;
    if (Array.isArray(tiers)) {
      for (const tier of tiers) {
        if (r >= tier.from) price = tier.price;
        else break; // from 升序，越过即不再命中
      }
    }
    return price;
  }

  return base; // 未知 mode → 恒价（旧数据安全）
}

/**
 * 逐件价格序列：该生该奖品已兑 `count` 件、本次再兑 `qty` 件的每件单价，
 * 长度 = qty，第 i 件（0 基）= 件序号 count+1+i 的单件价。
 *
 * 与 spec 计价函数同源：首件按 count 顺延、不重算已兑部分。
 */
export function priceSequence(
  base: number,
  escalation: PrizeEscalation | null | undefined,
  count: number,
  qty: number,
): number[] {
  const c = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
  const q = Number.isFinite(qty) ? Math.max(0, Math.floor(qty)) : 0;
  const prices: number[] = [];
  for (let i = 0; i < q; i++) {
    prices.push(escalationUnitPrice(base, escalation, c + 1 + i));
  }
  return prices;
}

/**
 * 校验累加配置是否合法、可算得单调/不亏的价。
 *
 * 返回错误文案数组，空数组 = 合法。非法配置会被奖品保存拦截（step<1、
 * 封顶 ≤ base、档位价回降、空表、首行 ≠ 基础价等），避免存出算得怪的规则。
 *
 * @param base 基础价（= 奖品 pointsRequired）
 * @param escalation 待校验配置；null/缺省 = 恒价，恒为合法
 */
export function validatePrizeEscalation(
  base: number,
  escalation: PrizeEscalation | null | undefined,
): string[] {
  const errors: string[] = [];
  if (!escalation) return errors; // 关 = 恒价，无规则可违

  if (escalation.mode === 'step') {
    const step = escalation.step;
    if (typeof step !== 'number' || !Number.isInteger(step) || step < 1) {
      errors.push('每多 1 件的加价（step）须为 ≥1 的整数');
      return errors;
    }
    const cap = escalation.cap;
    if (cap != null) {
      if (typeof cap !== 'number' || !Number.isInteger(cap) || cap < 1) {
        errors.push('封顶单价须为 ≥1 的整数');
        return errors;
      }
      // 封顶要“涨得到才有意义”：至少能出现一次加价（base+step）才可到顶
      if (cap < base + step) {
        errors.push(`封顶单价须 ≥ 基础价 + 加价（当前 ≤ ${base + step - 1} 时不会真正涨价，请设 ≥ ${base + step}）`);
        return errors;
      }
    }
    return errors;
  }

  if (escalation.mode === 'tiers') {
    const tiers = escalation.tiers;
    if (!Array.isArray(tiers) || tiers.length === 0) {
      errors.push('档位表至少需一行「第 1 件起 = 基础价」');
      return errors;
    }
    // 首行固定：from=1 且 price=基础价（基础价与首行同源，不出现两个价格来源）
    const first = tiers[0];
    if (first.from !== 1) {
      errors.push('档位表首行须为「从第 1 件起」');
      return errors;
    }
    if (first.price !== base) {
      errors.push('档位表首行单价须等于基础价（第 1 件 = 基础价）');
      return errors;
    }
    let prevPrice = first.price;
    for (let i = 1; i < tiers.length; i++) {
      const t = tiers[i];
      if (
        typeof t.from !== 'number' ||
        !Number.isInteger(t.from) ||
        t.from < 1 ||
        !Number.isFinite(t.price)
      ) {
        errors.push(`第 ${i + 1} 行起始件数与单价须为有效整数`);
        return errors;
      }
      if (t.from <= tiers[i - 1].from) {
        errors.push('档位表起始件数须逐行递增（升序，不能并列/回退）');
        return errors;
      }
      if (t.price < prevPrice) {
        errors.push('档位价只能升或平，不能回降');
        return errors;
      }
      prevPrice = t.price;
    }
    return errors;
  }

  errors.push('请选择计价模式（简单加价 / 档位表）');
  return errors;
}

/**
 * 该配置是否“真正会累加涨价”：
 * - 关 / 缺省 / 未知 mode → false（恒价）
 * - step（mode=step）→ true（step≥1 已由校验保证）
 * - tiers → 仅当分段 >1 行才算累加；单行 = 退化为恒价（不算累加，不显示标识）
 *
 * 与 `escalationSummary` 同口径：页面用它决定“是否走逐生报价/显示递增标识”，
 * 避免单行档位这种“开了但其实是恒价”的奖品被误当累加处理。
 */
export function escalationActive(
  escalation: PrizeEscalation | null | undefined,
): boolean {
  if (!escalation) return false;
  if (escalation.mode === 'step') return true;
  if (escalation.mode === 'tiers') {
    return Array.isArray(escalation.tiers) && escalation.tiers.length > 1;
  }
  return false;
}

/**
 * 累加规则的展示摘要（奖品卡/已选栏的“递增”标识文案）。
 * 仅用于老师选奖品时对“再兑更贵”有预期，非计价数据。
 *
 * - 未启用累加 / 单行档位（恒价）/ 未知 mode → null（不显示标识）
 * - step  → `第 2 件起 +{step}`，有封顶再拼 `· 封顶 {cap}`
 * - tiers → `第 {n} 件起 {price} 分`（第二段起头）
 */
export function escalationSummary(
  escalation: PrizeEscalation | null | undefined,
): string | null {
  if (!escalation) return null;

  if (escalation.mode === 'step') {
    const step =
      typeof escalation.step === 'number' && Number.isFinite(escalation.step)
        ? escalation.step
        : null;
    if (step === null) return '递增';
    const cap =
      escalation.cap != null && typeof escalation.cap === 'number'
        ? escalation.cap
        : null;
    return cap !== null ? `第 2 件起 +${step} · 封顶 ${cap}` : `第 2 件起 +${step}`;
  }

  if (escalation.mode === 'tiers') {
    const tiers = Array.isArray(escalation.tiers) ? escalation.tiers : [];
    if (tiers.length <= 1) return null; // 仅首行 = 未真正分段，退化为恒价
    const first = tiers[1];
    return `第 ${first.from} 件起 ${first.price} 分`;
  }

  return null;
}
